import { JSX } from 'react'
import { SEOProps } from '@/components/atoms/SEO/types'
import { getCurrentLang } from './getCurrentLang'
import { isProdSite } from './isProdSite'
import { removeFoldersFromUrl } from './removeFoldersFromUrl'
import { withTrailingSlash } from './withTrailingSlash'

export const generateMetaTags = (data: SEOProps) => {
  const robots = [
    data.set_noarchive ? 'noarchive' : '',
    data.set_nofollow ? 'nofollow' : '',
    data.set_noindex || !isProdSite(data.branch) ? 'noindex' : ''
  ].filter((tag) => tag !== '')

  const meta: JSX.IntrinsicElements['meta'][] = []
  const links: JSX.IntrinsicElements['link'][] = []

  if (data.canonical?.url) {
    links.push({ rel: 'canonical', href: withTrailingSlash(data.canonical.url) })
  } else if (data.page_slug) {
    const slug = data.page_slug.startsWith('/') ? data.page_slug.substring(1) : data.page_slug
    const fullUrlOfPage = process.env.GATSBY_URL + slug
    links.push({ rel: 'canonical', href: withTrailingSlash(fullUrlOfPage) })
  }

  if (data.location) {
    links.push({ rel: 'alternate', hrefLang: getCurrentLang(data?.location), href: data.location?.href })
  }

  if (data.alternates.length > 0) {
    data.alternates.forEach((alternate) => {
      const alternateLang = alternate.full_slug?.slice(0, 2)
      const href = process.env.GATSBY_URL + removeFoldersFromUrl(alternate.full_slug as string).substring(1)

      links.push({ rel: 'alternate', hrefLang: alternateLang, href: withTrailingSlash(href) })
    })
  }

  if (data.seo_description) {
    meta.push({ name: 'description', content: data.seo_description })
  }

  if (data.keywords) {
    meta.push({ name: 'keywords', content: data.keywords })
  }

  meta.push({ property: `og:title`, content: data.og_title || data.seo_title })

  if (data.og_description || data.seo_description) {
    meta.push({ property: `og:description`, content: data.og_description || data.seo_description })
  }

  if (data.og_image) {
    meta.push({ property: `og:image`, content: data.og_image.filename || `${process.env.GATSBY_URL}og_image.png` })
    meta.push({ property: `og:image:alt`, content: data.og_image?.alt })
    meta.push({ property: `og:image:width`, content: '1200' })
    meta.push({ property: `og:image:height`, content: '630' })
  }

  if (data.og_url?.url || data.og_url?.cached_url) {
    const ogUrl = data.og_url?.url
      ? data.og_url?.url
      : `${process.env.GATSBY_URL?.replace(/\/$/, '') + removeFoldersFromUrl(data.og_url?.cached_url || '')}`

    meta.push({ property: `og:url`, content: withTrailingSlash(ogUrl) })
  } else if (data.page_slug) {
    const slug = data.page_slug.startsWith('/') ? data.page_slug.substring(1) : data.page_slug
    const fullUrlOfPage = process.env.GATSBY_URL + slug

    meta.push({ property: 'og:url', content: withTrailingSlash(fullUrlOfPage) })
  }

  if (data.og_type) {
    meta.push({ property: `og:type`, content: data.og_type })
  }

  if (data.og_locale) {
    meta.push({ property: 'og:locale', content: data.og_locale })
  }

  meta.push({ property: `twitter:title`, content: data.og_title || data.seo_title })

  if (data.og_description || data.seo_description) {
    meta.push({ property: `twitter:description`, content: data.og_description || data.seo_description })
  }

  if (data.og_image) {
    meta.push({ property: `twitter:image`, content: data.og_image.filename || `${process.env.GATSBY_URL}og_image.png` })
  }

  if (data.twitter_creator) {
    meta.push({ property: 'twitter:creator', content: data.twitter_creator })
  }

  if (data.twitter_card) {
    meta.push({ property: 'twitter:card', content: data.twitter_card })
  }

  meta.push({ property: `robots`, content: robots.join(', ') })

  return { meta, links }
}
